import {
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputBase,
    Paper,
    Popover,
    Tooltip,
    Typography,
} from "@mui/material";
import { AlertTriangle, Search, Settings, X } from "lucide-react";
import type React from "react";
import { useState } from "react";

export interface SearchField {
    fieldName: string;
    displayName: string;
    default?: boolean;
}

export const SEARCH_FIELDS: SearchField[] = [
    { fieldName: "id", displayName: "Case ID", default: true },
    { fieldName: "clientName", displayName: "Client Name", default: true },
    { fieldName: "phoneNumber", displayName: "Client Phone", default: true },
    { fieldName: "emailAddress", displayName: "Client Email" },
    { fieldName: "claimNumber", displayName: "Claim Number" },
    { fieldName: "dateOfIncident", displayName: "Date of Incident" },
];

interface SearchableToolbarProps {
    searchFields: SearchField[];
    globalFilter: string;
    onGlobalFilterChange: (value: string) => void;
    selectedSearchFields: string[];
    onSelectedSearchFieldsChange: (fields: string[]) => void;
    placeholder?: string;
    maxWidth?: number;
    performanceWarningThreshold?: number;
}

const SearchableToolbar: React.FC<SearchableToolbarProps> = ({
    searchFields,
    globalFilter,
    onGlobalFilterChange,
    selectedSearchFields,
    onSelectedSearchFieldsChange,
    placeholder = "Search",
    maxWidth = 400,
    performanceWarningThreshold = 5,
}) => {
    const [searchFieldsAnchor, setSearchFieldsAnchor] = useState<HTMLElement | null>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onGlobalFilterChange(event.target.value);
    };

    const handleClear = () => {
        onGlobalFilterChange("");
    };

    const handleSearchFieldsOpen = (event: React.MouseEvent<HTMLElement>) => {
        setSearchFieldsAnchor(event.currentTarget);
    };

    const handleSearchFieldsClose = () => {
        setSearchFieldsAnchor(null);
    };

    const handleSearchFieldToggle = (fieldName: string) => {
        const newSearchFields = selectedSearchFields.includes(fieldName)
            ? selectedSearchFields.filter((f) => f !== fieldName)
            : [...selectedSearchFields, fieldName];

        onSelectedSearchFieldsChange(newSearchFields);
    };

    const handleSelectAllFields = () => {
        onSelectedSearchFieldsChange(searchFields.map((field) => field.fieldName));
    };

    const handleClearAllFields = () => {
        onSelectedSearchFieldsChange([]);
    };

    return (
        <>
            <Paper
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    maxWidth,
                    borderRadius: "6px",
                    padding: (theme) => theme.spacing(0.5, 1),
                    boxShadow: "none",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                <IconButton size="small" sx={{ p: "4px" }}>
                    <Search size={18} />
                </IconButton>
                <InputBase
                    placeholder={placeholder}
                    value={globalFilter}
                    onChange={handleInputChange}
                    fullWidth
                    sx={{
                        flex: 1,
                        fontSize: "0.875rem",
                        "& .MuiInputBase-input": {
                            padding: (theme) => theme.spacing(1, 0),
                        },
                    }}
                />
                {globalFilter && (
                    <IconButton size="small" onClick={handleClear} sx={{ p: "4px" }}>
                        <X size={16} />
                    </IconButton>
                )}
                <Tooltip title="Search Field Options">
                    <IconButton
                        size="small"
                        onClick={handleSearchFieldsOpen}
                        sx={{ p: "4px", ml: 0.5 }}
                    >
                        <Settings size={16} />
                    </IconButton>
                </Tooltip>
            </Paper>

            <Popover
                open={Boolean(searchFieldsAnchor)}
                anchorEl={searchFieldsAnchor}
                onClose={handleSearchFieldsClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                sx={{
                    "& .MuiPaper-root": {
                        padding: (theme) => theme.spacing(2),
                        maxWidth: 350,
                        minWidth: 300,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: (theme) => theme.spacing(1),
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            marginBottom: (theme) => theme.spacing(1),
                            color: (theme) => theme.palette.text.primary,
                        }}
                    >
                        Search Fields ({selectedSearchFields.length} of {searchFields.length}{" "}
                        selected)
                    </Typography>

                    {selectedSearchFields.length > performanceWarningThreshold && (
                        <Box
                            sx={{
                                backgroundColor: (theme) => `${theme.palette.warning.light}20`,
                                border: (theme) => `1px solid ${theme.palette.warning.main}`,
                                borderRadius: (theme) => theme.shape.borderRadius,
                                padding: (theme) => theme.spacing(1.5),
                                marginBottom: (theme) => theme.spacing(2),
                                display: "flex",
                                alignItems: "flex-start",
                                gap: (theme) => theme.spacing(1),
                            }}
                        >
                            <AlertTriangle size={16} />
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: (theme) => theme.palette.warning.dark,
                                    lineHeight: 1.4,
                                }}
                            >
                                Selecting many search fields may impact search performance. Consider
                                using fewer fields for faster results.
                            </Typography>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: (theme) => theme.spacing(0.5),
                        }}
                    >
                        {searchFields.map((field) => (
                            <FormControlLabel
                                key={field.fieldName}
                                sx={{
                                    margin: 0,
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                        lineHeight: 1.2,
                                    },
                                    "& .MuiCheckbox-root": {
                                        padding: (theme) => theme.spacing(0.5),
                                    },
                                }}
                                control={
                                    <Checkbox
                                        checked={selectedSearchFields.includes(field.fieldName)}
                                        onChange={() => handleSearchFieldToggle(field.fieldName)}
                                        size="small"
                                    />
                                }
                                label={field.displayName}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: (theme) => theme.spacing(1),
                            paddingTop: (theme) => theme.spacing(1),
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            Quick Actions:
                        </Typography>
                        <Box>
                            <IconButton
                                onClick={handleSelectAllFields}
                                disabled={selectedSearchFields.length === searchFields.length}
                                size="small"
                                sx={{
                                    fontSize: "0.75rem",
                                    padding: (theme) => theme.spacing(0.5, 1),
                                    borderRadius: (theme) => theme.shape.borderRadius,
                                    "&:hover": {
                                        backgroundColor: (theme) => theme.palette.action.hover,
                                    },
                                }}
                            >
                                <Typography variant="caption">Select All</Typography>
                            </IconButton>
                            <IconButton
                                onClick={handleClearAllFields}
                                disabled={selectedSearchFields.length === 0}
                                size="small"
                                sx={{
                                    fontSize: "0.75rem",
                                    padding: (theme) => theme.spacing(0.5, 1),
                                    borderRadius: (theme) => theme.shape.borderRadius,
                                    "&:hover": {
                                        backgroundColor: (theme) => theme.palette.action.hover,
                                    },
                                }}
                            >
                                <Typography variant="caption">Clear All</Typography>
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default SearchableToolbar;
